// Filter-context configuration for issue-analysis (IA_ABSORPTION_PLAN Phase 4 step 11). All project
// SQL for its two filter contexts lives here; `createFilterContext` (@rdrive/data-filters) holds
// none. dashboard.config.js passes both configs as its `filters`, and the dashboard builds one
// context per entry (`filters.issues`, `filters.statusHistory`; the store's `filterContext` /
// `filterStatusHistoryContext` delegates). Both are initialised by the data loader's onLoaded, with
// DuckDB's query gate open.
//
// They replace this app's own context classes, `FilterIssuesContext` and
// `FilterStatusHistoryContext` (deleted). Each SELECT and the column detection below are copied
// verbatim from those classes, so the parity harness (`tooling/parity/filter-context/`, which drives
// both against copies of the classes) compares like for like.

// The optional BI-* columns `filteredIssues` exposes, as [issues column, view alias]. They come
// from the dynamic tag-pivot endpoint, so a column only exists if at least one issue in the project
// carries that field tag.
const BI_FIELDS = [
  ['BI-Issue-Type', 'issueType'],
  ['BI-Issue-Category', 'issueCategory'],
  ['BI-Issue-Item', 'issueItem'],
  ['BI-Responsible-Party', 'responsibleParty'],
  ['BI-Photo', 'photo'],
  ['BI-Priority', 'priority'],
  ['BI-DueBy', 'dueBy'],
  ['BI-Response-Photo', 'responsePhoto'],
  ['BI-Issue-Description', 'issueDescription'],
];

/**
 * The issues context's `detect` (run by createFilterContext at the start of every init): which
 * API version the loaded `issues` table comes from, and which optional columns it has.
 * `conn.queryRaw` bypasses DuckDBService's isDataLoaded gate and throws on error instead of
 * shutting the gate — as the class did. The two log lines keep the class's tag, so the console
 * reads as it did.
 * @returns {Promise<{ columns: string[], isLegacyApi: boolean, hasUserAssigneeCol: boolean,
 *   hasNewAssigneeColumns: boolean }>}
 */
const detectIssueColumns = async ({ conn, sourceTable }) => {
  const colResult = await conn.queryRaw(`SELECT column_name FROM information_schema.columns WHERE table_name = '${sourceTable}'`);
  const columns = colResult.toArray().map(r => r.toJSON().column_name);

  const isLegacyApi = columns.includes('IssueAssignee');
  const hasUserAssigneeCol = columns.includes('IssueUserAssignees');

  console.log('[FilterIssuesContext] Columns:', columns);
  console.log('[FilterIssuesContext] API version:', isLegacyApi ? 'LEGACY' : 'NEW', 'hasUserAssignees:', hasUserAssigneeCol);

  return { columns, isLegacyApi, hasUserAssigneeCol, hasNewAssigneeColumns: !isLegacyApi && hasUserAssigneeCol };
};

/**
 * True when the loaded `issues` table has the NEW API's separate user/team assignee columns
 * (no legacy `IssueAssignee`, and `IssueUserAssignees` present) — what `FilterIssuesContext`
 * exposed as its `hasNewAssigneeColumns` property. Read off the context's last detection, so it is
 * `false` before the context is initialised (the property was undefined then), and NOT reactive
 * (the class's property was not either).
 * @param {object | null | undefined} filterContext  the issues context (`useDashboard().filters.issues`)
 */
export const hasNewAssigneeColumns = (filterContext) =>
  filterContext?.detected?.hasNewAssigneeColumns ?? false;

// THE team-assignee rule: the legacy API's `IssueAssignee`, else the new API's `IssueTeamAssignee`
// — whether or not the table also has `IssueUserAssignees`. `filteredIssues.issueAssignee` and
// `teamAssigneeColumn` both use it, so SQL over the raw `issues` table reads the same column the
// view exposes.
const teamAssigneeColumnOf = ({ isLegacyApi }) => (isLegacyApi ? 'IssueAssignee' : 'IssueTeamAssignee');

/**
 * The `issues` column holding an issue's assigned TEAM, for SQL that reads the raw `issues` table
 * (the view's `issueAssignee` is the same column): `IssueAssignee` when the context's last
 * detection found the legacy API, else `IssueTeamAssignee`. Before any detection — never the case
 * for its readers, which run after the load — the view's own default (`IssueTeamAssignee`).
 *
 * Replaces the readers' old `hasNewAssigneeColumns(ctx) ? 'IssueTeamAssignee' : 'IssueAssignee'`,
 * which picked `IssueAssignee` for a NEW-API table without `IssueUserAssignees` — a column that
 * table does not have (Binder Error; the filter was not applied). IA_ABSORPTION_PLAN Phase 4 step 13.
 * Read live, like `hasNewAssigneeColumns` (`detected` is not reactive).
 * @param {object | null | undefined} filterContext  the issues context (`useDashboard().filters.issues`)
 * @returns {'IssueAssignee' | 'IssueTeamAssignee'}
 */
export const teamAssigneeColumn = (filterContext) =>
  teamAssigneeColumnOf(filterContext?.detected || NOTHING_DETECTED);

/**
 * One optional `BI-*` column of the RAW `issues` table, for SQL that reads `issues` directly
 * rather than `filteredIssues` (whose view already applies this rule per column): the quoted
 * column (`i."BI-DueBy"`) when the issues context's last detection found it, else a typed NULL —
 * a project whose issues carry no such tag has no such column, and naming it is a Binder Error
 * (the filter built on it is then never applied). `column` is a trusted constant (one of
 * BI_FIELDS), never a value. Read live, like `teamAssigneeColumn` (`detected` is not reactive).
 * Used by the FAB's "overdue only" filter (composables/useIssueStatusFilters.js) since
 * IA_ABSORPTION_PLAN Phase 4 step 13.2.
 * @param {object | null | undefined} filterContext  the issues context (`dashboard.filters.issues`)
 * @param {string} column  e.g. 'BI-DueBy'
 * @param {string} [alias='i']  the `issues` alias in the caller's query
 * @returns {string}
 */
export const biColumnSql = (filterContext, column, alias = 'i') =>
  (filterContext?.detected?.columns || []).includes(column)
    ? `${alias}."${column}"`
    : 'CAST(NULL AS VARCHAR)';

// filteredIssues' assignee columns: the legacy `IssueAssignee` or the new `IssueTeamAssignee`, and
// the user assignees or a typed NULL.
const assigneeColumns = ({ isLegacyApi, hasUserAssigneeCol }) => {
  const teamAssigneeCol = teamAssigneeColumnOf({ isLegacyApi });
  const userAssigneeExpr = hasUserAssigneeCol
    ? `i.IssueUserAssignees AS issueUserAssignees,`
    : `CAST(NULL AS VARCHAR) AS issueUserAssignees,`;
  return `i.${teamAssigneeCol} AS issueAssignee,
          ${userAssigneeExpr}`;
};

// filteredIssues' BI-* columns: NULL for any that are absent, otherwise CREATE VIEW throws
// "Referenced column ... not found" and the whole view (and every chart on it) fails to build.
const biColumns = ({ columns }) =>
  BI_FIELDS
    .map(([col, alias]) =>
      columns.includes(col)
        ? `i."${col}" AS ${alias}`
        : `CAST(NULL AS VARCHAR) AS ${alias}`)
    .join(',\n          ');

// Before any detection ran (not reachable: `detect` always runs first) — the class's view with no
// optional column found.
const NOTHING_DETECTED = { columns: [], isLegacyApi: false, hasUserAssigneeCol: false };

/**
 * Issues filter context — app-wide, initialised at the data load. Single VARCHAR key `Id`, unique
 * index, base = every issue (the FAB's status and "overdue only" filters are filter views, not a
 * base restriction). `filteredIssues` follows the detected columns.
 */
export const issuesFilterConfig = {
  id: 'issues',
  sourceTable: 'issues',
  contextTable: 'filter_context',
  keyColumns: ['Id'],
  uniqueIndex: true,
  detect: detectIssueColumns,

  views: [
    {
      name: 'filteredIssues',
      select: ({ contextTable, detected }) => {
        const d = detected || NOTHING_DETECTED;
        return `
      SELECT
          i.Id AS id,
          i.IssueTemplateId AS issueTemplateId,
          i.IssueClientId AS issueClientId,
          i.IssueLocationId AS issueLocationId,
          ${assigneeColumns(d)}
          i.IssueControlledByTeamId as issueControlledByTeamId,
          i.IssueCreatedByTeamId as issueCreatedByTeamId,
          i.IssueCreatedByUserId as issueCreatedByUserId,
          i.IssueStatusId AS issueStatusId,
          i.IssueLastUpdated AS issueLastUpdated,
          ${biColumns(d)}
      FROM issues i
      INNER JOIN ${contextTable} fc USING(Id)`;
      },
    },
  ],
};

/**
 * Status-history filter context — initialised at the data load, after the issues context
 * (`status_history_temporal` is the manifest's ddl.post). Composite key; base = every row.
 */
export const statusHistoryFilterConfig = {
  id: 'statusHistory',
  sourceTable: 'status_history_temporal',
  contextTable: 'status_history_filter_context',
  keyColumns: [
    { name: 'subItemId', type: 'VARCHAR' },
    { name: 'validFrom', type: 'TIMESTAMP' },
    { name: 'statusId', type: 'VARCHAR' },
  ],

  views: [
    {
      // The trailing comma after `validTo` is the class's, verbatim (DuckDB accepts it).
      name: 'filteredStatusHistory',
      select: ({ contextTable }) => `
      SELECT
          sht.statusId AS statusId,
          i.IssueLocationId as locationId,
          i.IssueTemplateId as templateId,
          sht.createdByTeamId AS createdByTeamId,
          sht.createdByUserId AS createdByUserId,
          sht.subItemId AS subItemId,
          sht.validFrom AS validFrom,
          sht.validTo AS validTo,
      FROM status_history_temporal sht
      INNER JOIN ${contextTable} fc ON
          sht.subItemId = fc.subItemId AND
          sht.validFrom = fc.validFrom AND
          sht.statusId = fc.statusId
      INNER JOIN issues i on sht.subItemId = i.Id`,
    },
  ],
};
